import { useEffect, useRef, useState } from "react";
import { Bot, X, Send, Phone, MessageCircle } from "lucide-react";
import { streamSSE } from "@/lib/sse";
import { PHONE_TEL, WHATSAPP_URL } from "@/siteConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUGGESTIONS = ["ما هي خدماتكم؟", "هل تفتحون باب شقة مقفل؟", "ما مناطق التغطية في جدة؟"];

const ChatWidget = () => {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [input, setInput] = useState("");
    const [messages, setMessages] = useState([
        {
            role: "assistant",
            content:
                "أهلاً بك في المفتاح الذهبي! اسألني عن خدماتنا، مناطق التغطية في جدة، أو أوقات العمل.",
        },
    ]);
    const listRef = useRef(null);
    const sessionRef = useRef(null);
    if (!sessionRef.current) {
        sessionRef.current =
            localStorage.getItem("mk_chat_session") || crypto.randomUUID();
        localStorage.setItem("mk_chat_session", sessionRef.current);
    }

    useEffect(() => {
        listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
    }, [messages, open]);

    const send = async (preset) => {
        const msg = (preset ?? input).trim();
        if (!msg || busy) return;
        setInput("");
        setBusy(true);
        setMessages((m) => [
            ...m,
            { role: "user", content: msg },
            { role: "assistant", content: "" },
        ]);
        try {
            await streamSSE(
                `${API}/chat`,
                { session_id: sessionRef.current, message: msg },
                (delta) =>
                    setMessages((m) => {
                        const copy = [...m];
                        copy[copy.length - 1] = {
                            role: "assistant",
                            content: copy[copy.length - 1].content + delta,
                        };
                        return copy;
                    }),
            );
        } catch {
            setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                    role: "assistant",
                    content: "عذراً، تعذر الاتصال حالياً — اتصل بنا مباشرة على 0537706725",
                };
                return copy;
            });
        }
        setBusy(false);
    };

    return (
        <>
            <button
                data-testid="chat-fab"
                onClick={() => setOpen((o) => !o)}
                aria-label="افتح مساعد المحادثة"
                className="fixed bottom-24 left-4 z-[70] flex h-14 w-14 items-center justify-center rounded-full bg-amber-500 text-[#0B0F17] shadow-[0_10px_30px_rgba(245,158,11,0.4)] transition-transform duration-300 hover:scale-110 md:bottom-6 md:left-6"
            >
                {open ? <X size={24} /> : <Bot size={26} />}
            </button>

            {open && (
                <div
                    data-testid="chat-panel"
                    className="fixed bottom-40 left-4 z-[70] flex h-[26rem] w-[calc(100vw-2rem)] max-w-sm flex-col overflow-hidden rounded-3xl border border-[#232F45] bg-[#121824] shadow-[0_30px_80px_rgba(0,0,0,0.6)] md:bottom-24 md:left-6"
                >
                    <div className="flex items-center justify-between border-b border-white/5 bg-[#151C2A] px-4 py-3">
                        <div className="flex items-center gap-3">
                            <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-amber-500/15 text-amber-400">
                                <Bot size={20} />
                            </span>
                            <div>
                                <p className="text-sm font-extrabold text-white">مساعد المفتاح الذهبي</p>
                                <p className="flex items-center gap-1.5 text-[11px] text-emerald-400">
                                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                                    متصل الآن
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <a href={PHONE_TEL} data-testid="chat-call-link" aria-label="اتصال" className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-600/15 text-blue-400 transition-colors hover:bg-blue-600/30">
                                <Phone size={16} />
                            </a>
                            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="chat-whatsapp-link" aria-label="واتساب" className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-600/15 text-emerald-400 transition-colors hover:bg-emerald-600/30">
                                <MessageCircle size={16} />
                            </a>
                        </div>
                    </div>

                    <div ref={listRef} data-testid="chat-messages" className="flex-1 space-y-3 overflow-y-auto p-4">
                        {messages.map((m, i) => (
                            <div key={i} data-testid={`chat-message-${i}`} className={`flex ${m.role === "user" ? "justify-start" : "justify-end"}`}>
                                <p className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                                    m.role === "user"
                                        ? "bg-amber-500 font-bold text-[#0B0F17]"
                                        : "bg-[#1A2332] text-gray-200"
                                }`}>
                                    {m.content || (busy ? "…" : "")}
                                </p>
                            </div>
                        ))}
                        {messages.length === 1 && (
                            <div className="flex flex-wrap gap-2 pt-2">
                                {SUGGESTIONS.map((s, i) => (
                                    <button
                                        key={s}
                                        data-testid={`chat-suggestion-${i}`}
                                        onClick={() => send(s)}
                                        className="rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1.5 text-xs font-bold text-amber-400 transition-colors hover:bg-amber-500/20"
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <form
                        className="flex items-center gap-2 border-t border-white/5 p-3"
                        onSubmit={(e) => {
                            e.preventDefault();
                            send();
                        }}
                    >
                        <input
                            data-testid="chat-input"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="اكتب سؤالك هنا…"
                            className="h-11 flex-1 rounded-xl border border-[#232F45] bg-[#0B0F17] px-4 text-sm text-white placeholder:text-gray-600 focus:border-amber-500 focus:outline-none"
                        />
                        <button
                            type="submit"
                            data-testid="chat-send-button"
                            disabled={busy}
                            aria-label="أرسل"
                            className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-500 text-[#0B0F17] transition-colors hover:bg-amber-400 disabled:opacity-50"
                        >
                            <Send size={18} />
                        </button>
                    </form>
                </div>
            )}
        </>
    );
};

export default ChatWidget;
